import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import {
  compareShifts,
  formatShiftLabel,
  hasShiftStarted,
  isShiftFull,
  shiftJoinOptions,
  type EventShift,
} from '@/utils/eventShifts';

interface EventPreviewYourShiftsProps {
  /** Every shift on the event; the ones marked `mine` are listed. */
  shifts: EventShift[];
  onChange: (shift: EventShift) => void;
  onLeave: (shift: EventShift) => void;
  /** Opens the picker for adding more shifts. */
  onPickMore: () => void;
  disabled?: boolean;
}

const actionStyles = {
  flex: '1',
  py: '2',
  borderRadius: 'lg',
  fontSize: 'sm',
  fontWeight: 'semibold',
  cursor: 'pointer',
  border: '1px solid',
  bg: 'white',
} as const;

/**
 * The volunteer's own shifts, each with Change and Leave in plain sight.
 *
 * This sits directly under the volunteer card because that is where people look for "my
 * shift". The first version put Leave at the end of a row in the event's full shift list,
 * where nobody found it -- and hid it without a word whenever it could not be used. Here a
 * shift that can no longer be changed says why instead.
 */
export function EventPreviewYourShifts({
  shifts,
  onChange,
  onLeave,
  onPickMore,
  disabled = false,
}: EventPreviewYourShiftsProps) {
  const mine = shifts.filter((s) => s.mine).sort(compareShifts);
  const now = new Date();
  // Only offered when there is somewhere to go: an upcoming shift they are not on, with room.
  const canPickMore = shiftJoinOptions(shifts, now).options.some((s) => !isShiftFull(s));

  const pickButton = canPickMore && (
    <chakra.button
      type="button"
      w="full"
      mt="2"
      py="2.5"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap="1.5"
      borderRadius="xl"
      border="1px dashed"
      borderColor="volyx.action"
      bg="transparent"
      color="volyx.action"
      fontSize="sm"
      fontWeight="semibold"
      cursor="pointer"
      disabled={disabled}
      onClick={onPickMore}
    >
      <Plus size={16} />
      {mine.length === 0 ? 'Pick shifts' : 'Pick more shifts'}
    </chakra.button>
  );

  return (
    <Box px={{ base: '5', md: '0' }} mb="4">
      <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" textTransform="uppercase" mb="2">
        Your shifts
      </Text>

      {mine.length === 0 ? (
        <Box bg="volyx.lightGray" borderRadius="xl" px="4" py="3">
          <Text fontSize="sm" color="volyx.textSecondary" m="0">
            {canPickMore
              ? 'You are not on any shift yet.'
              : 'You are not on any shift yet, and every upcoming shift is full.'}
          </Text>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap="2">
          {mine.map((shift) => {
            const started = hasShiftStarted(shift, now);
            const ended = new Date(shift.ends_at).getTime() <= now.getTime();
            return (
              <Box key={shift.id} bg="volyx.lightGray" borderRadius="xl" px="4" py="3">
                <Text fontSize="sm" fontWeight="semibold" color="volyx.text" m="0" mb={started ? '0.5' : '2.5'}>
                  {formatShiftLabel(shift)}
                </Text>
                {started ? (
                  <Text fontSize="xs" color="volyx.textSecondary" m="0">
                    {ended
                      ? 'This shift is over.'
                      : 'In progress. It can no longer be changed or dropped — tell the organizer in the event chat.'}
                  </Text>
                ) : (
                  <Flex gap="2">
                    <chakra.button
                      type="button"
                      {...actionStyles}
                      borderColor="volyx.action"
                      color="volyx.action"
                      disabled={disabled}
                      aria-label={`Change shift ${formatShiftLabel(shift)}`}
                      onClick={() => onChange(shift)}
                    >
                      Change shift
                    </chakra.button>
                    <chakra.button
                      type="button"
                      {...actionStyles}
                      borderColor="volyx.mediumGray"
                      color="volyx.error"
                      disabled={disabled}
                      aria-label={`Leave shift ${formatShiftLabel(shift)}`}
                      onClick={() => onLeave(shift)}
                    >
                      Leave
                    </chakra.button>
                  </Flex>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {pickButton}
    </Box>
  );
}
